import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import AudioPlayer from "./components/AudioPlayer"; // Import AudioPlayer component
import "./DynamicPage.css";
import UpcomingEvents from "./components/UpcomingEvents";
// import CalendarView from "./components/CalendarView";
import PrayerForm from "./components/PrayerForm";
import VideoPlaylist from "./components/VideoPlaylist";
import BlogPosts from "./components/BlogPosts";
import PayPalDonation from "./components/PayPalDonation";
import VenmoButton from "./components/VenmoButton";
import ContactForm from "./components/ContactForm"; // Import the CSS file

const DynamicPage = ({ adminPageData = null }) => {
    const location = useLocation();
    const [pageContent, setPageContent] = useState(null);
    const [processedLayout, setProcessedLayout] = useState(null);

    useEffect(() => {
        const fullSlug = location.pathname.slice(1); // Remove leading `/`

        // Check if coming from the admin panel
        if (fullSlug === "admin" && adminPageData) {
            console.log("Using admin-provided page data");
            processPageData(adminPageData);
            return;
        }

        // Cancel token for Axios
        const source = axios.CancelToken.source();

        axios
            .get(`/api/pages/${fullSlug}`, { cancelToken: source.token })
            .then((response) => {
                console.log("API Response:", response.data);
                processPageData(response.data);
            })
            .catch((error) => {
                if (axios.isCancel(error)) {
                    console.log("Previous request canceled:", error.message);
                } else {
                    console.error("Error fetching page:", error);
                }
            });

        return () => {
            source.cancel("API call canceled due to component unmount or slug change.");
        };
    }, [location, adminPageData]);

        const processPageData = (data) => {

        const sectionsJSX = data.sections
            .map((section, index) => {
                if (!section || !section.type) {
                    console.warn("Skipping invalid section:", section);
                    return null;
                }

                switch (section.type) {
                    case "image":
                        return (
                            <img
                                key={`${index}-${section.type}`}
                                src={section.src}
                                alt={section.alt}
                                width={section.width}
                                height={section.height}
                                className={`dynamic-image ${section.className || ""}`}
                                style={section.style || {}}
                            />
                        );
                    case "text":
                        return (
                            <p
                                key={`${index}-${section.type}`}
                                className={section.className || ""}
                                style={section.style || {}}
                                dangerouslySetInnerHTML={{ __html: section.content }}
                            ></p>
                        );
                    case "quote":
                        return (
                            <blockquote
                                key={`${index}-${section.type}`}
                                className={section.className || ""}
                                style={section.style || {}}
                                dangerouslySetInnerHTML={{ __html: section.content }}
                            ></blockquote>
                        );
                    case "line-break":
                        return <br key={`${index}-${section.type}`} />;
                    case "horizontal-rule":
                        return (
                            <hr
                                key={`${index}-${section.type}`}
                                className={section.className || ""}
                                style={section.style || {}}
                            />
                        );
                    case "section":
                        return (
                            <div key={`${index}-${section.type}`} className={section.className || ""}>
                                <h3
                                    className="text-xl font-semibold text-red-900"
                                    dangerouslySetInnerHTML={{ __html: section.title }}
                                ></h3>
                                <p
                                    className="mt-2"
                                    dangerouslySetInnerHTML={{ __html: section.content }}
                                ></p>
                                {section.additional && (
                                    <p
                                        className="mt-2 text-sm text-gray-700"
                                        dangerouslySetInnerHTML={{ __html: section.additional }}
                                    ></p>
                                )}
                            </div>
                        );

                    case "list":
                        const ListTag = section.listType === "ordered" ? "ol" : "ul";
                        return (
                            <ListTag
                                key={`${index}-${section.type}`}
                                className={`list-container ${section.className || ""}`} // Ensure list-specific className
                                style={section.style || {}} // Allow inline styles for customization
                            >
                                {section.items.map((item, i) => (
                                    <li
                                        key={i}
                                        className="list-item" // Add item-specific className for better control
                                        dangerouslySetInnerHTML={{ __html: item }}
                                    ></li>
                                ))}
                            </ListTag>
                        );

                    case "accordion":
                        return (
                            <div key={`${index}-${section.type}`} className={`accordion ${section.className || ""}`}>
                                {section.items.map((item, i) => (
                                    <div key={i} className="accordion-item">
                                        <div
                                            className="accordion-header"
                                            dangerouslySetInnerHTML={{ __html: item.title }}
                                        ></div>
                                        <div
                                            className="accordion-body"
                                            dangerouslySetInnerHTML={{ __html: item.content }}
                                        ></div>
                                    </div>
                                ))}
                            </div>
                        );

                    case "card":
                        return (
                            <div
                                key={`${index}-${section.type}`}
                                className={`card ${section.className || ""}`}
                                style={section.style || {}}
                            >
                                {section.image && (
                                    <img
                                        src={section.image.src}
                                        alt={section.image.alt}
                                        className={section.image.className || ""}
                                        style={section.image.style || {}}
                                    />
                                )}
                                <div className="card-body">
                                    <h3
                                        className="card-title"
                                        dangerouslySetInnerHTML={{ __html: section.title }}
                                    ></h3>
                                    <p
                                        className="card-body-text"
                                        dangerouslySetInnerHTML={{ __html: section.body }}
                                    ></p>
                                </div>
                            </div>
                        );
                    case "audio":
                        return (
                            <AudioPlayer
                                key={`${index}-${section.type}`}
                                title={section.title}
                                playlist={section.playlist}
                                className={section.className || ""}
                            />
                        );

                    case "upcomingEvents":
                        return (
                            <div key={`${index}-${section.type}`} className={`upcoming-events ${section.className || ""}`}>
                                <h3 className="text-xl font-semibold text-red-900">{section.title}</h3>
                                <p className="text-base text-gray-700">{section.description}</p>
                                <UpcomingEvents eventCount={section.eventCount || 5} />
                            </div>
                        );

                    case "iframe":
                        return (
                            <iframe
                                key={index}
                                src={section.src}
                                style={section.style}
                                className={section.className || ""}
                            ></iframe>
                        );

                    case "prayer-form":
                        return (
                            <div
                                key={`${index}-${section.type}`}
                                className={section.className || "prayer-form-section"}
                            >
                                <PrayerForm />
                            </div>
                        );

                    case "videoPlaylist":
                        return (
                            <div key={index} className="video-playlist-section my-6">
                                <VideoPlaylist videos={section.videos} />
                            </div>
                        );

                    case "blogPosts":
                        return (
                            <div key={`${index}-${section.type}`} className={section.className || ""}>
                                <BlogPosts />
                            </div>
                        );

                    case "paypal-donation":
                        return (
                            <PayPalDonation
                                key={index}
                                clientId={section.clientId}
                                currency={section.currency || "USD"}
                                amount={section.amount || "10.00"}
                            />
                        );
                    case "venmo-button":
                        return (
                            <VenmoButton
                                key={index}
                                username={section.username}
                                amount={section.amount || "10.00"}
                                note={section.note || "Donation for Solid Rock Cathedral"}
                            />
                        );

                    case "map":
                        return (
                            <div className={section.className || "map-container"}>
                                <iframe
                                    src={section.embedUrl}
                                    width={section.width || "100%"}
                                    height={section.height || "450px"}
                                    style={{ border: "0" }}
                                    allowFullScreen=""
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                ></iframe>
                            </div>
                        );
                    case "contact-form":
                        return (
                            <div key={index} className={section.className || "contact-form-section"}>
                                <ContactForm />
                            </div>
                        );

                    default:
                        console.warn(`Unknown section type: ${section.type}`);
                        return null;
                }
            })
            .filter(Boolean);

        setProcessedLayout(
            <div className="dynamic-page">
                <h1 className="text-5xl">{data.title}</h1>
                {sectionsJSX}
            </div>
        );

        setPageContent(data);
    }

    if (!pageContent || !processedLayout) return <div>Loading...</div>;

    // Define transition properties
    const pageTransition = {
        initial: { opacity: 0, y: 0 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: 0 },
        transition: { duration: 1.0 }
    };

    return (
        <motion.div {...pageTransition}>
            {processedLayout}
        </motion.div>
    );
};

export default DynamicPage;
